import React, { useState, useEffect, useCallback } from 'react';

interface TaskStep {
  step_id: string; order: number; description: string;
  suggested_model: string; selected_model: string; status: string;
  tool_used: string; logs: string[]; artifacts: string[]; phase: string;
}

interface Task {
  task_id: string; prompt: string; status: string; profile: string;
  plan: TaskStep[]; planner_thoughts: string;
}

type Phase = 'idle' | 'planning' | 'awaiting_approval' | 'executing' | 'completed';

const MODELS_FALLBACK = [
  { id: 'auto', name: '🤖 Auto (Planner decides)' },
  { id: 'meta/llama-3.1-8b-instruct', name: '⚡ Llama 3.1 8B' },
  { id: 'nvidia/llama-3.3-nemotron-super-49b-v1', name: '🧠 Nemotron 49B' },
];

const PROFILES = [
  { id: 'safe',      name: 'Safe',      emoji: '🛡️', desc: 'Zatwierdź każdy krok' },
  { id: 'balanced',  name: 'Balanced',  emoji: '⚖️', desc: 'Zatwierdź co fazę' },
  { id: 'research',  name: 'Research',  emoji: '🔍', desc: 'Głęboka analiza' },
  { id: 'checklist', name: 'Checklist', emoji: '✅', desc: 'Lista kroków' },
  { id: 'yolo',      name: 'YOLO',      emoji: '🚀', desc: 'Auto bez pytań' },
  { id: 'custom',    name: 'Custom',    emoji: '⚙️', desc: 'Konfigurowalny' },
];

const STATUS_COLOR: Record<string, string> = {
  finished: 'text-green-400 bg-green-500/10 border-green-500/30',
  running:  'text-blue-400  bg-blue-500/10  border-blue-500/30',
  failed:   'text-red-400   bg-red-500/10   border-red-500/30',
  approved: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30',
  pending:  'text-slate-400  bg-slate-500/10 border-slate-500/30',
  retry:    'text-orange-400 bg-orange-500/10 border-orange-500/30',
  skipped:  'text-slate-600  bg-slate-900    border-slate-700',
};

const tryParseArtifact = (raw: string): { type: 'json' | 'code' | 'text'; content: string } => {
  const trimmed = raw.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try { return { type: 'json', content: JSON.stringify(JSON.parse(trimmed), null, 2) }; } catch {}
  }
  const codeKeywords = ['def ', 'import ', 'class ', 'function ', 'const ', 'async ', 'from ', '#!', 'curl ', 'npm '];
  if (codeKeywords.some(k => trimmed.includes(k))) return { type: 'code', content: trimmed };
  return { type: 'text', content: trimmed };
};

const ArtifactBlock: React.FC<{ raw: string; index: number }> = ({ raw }) => {
  const [collapsed, setCollapsed] = useState(true);
  const parsed = tryParseArtifact(raw);
  const preview = parsed.content.slice(0, 120).replace(/\n/g, ' ');
  return (
    <div className="mt-1 border border-[#2a2e39] rounded overflow-hidden">
      <button onClick={() => setCollapsed(v => !v)}
        className="w-full flex items-center gap-2 px-2 py-1 bg-[#131722] hover:bg-[#1a1d2e] transition-colors text-left">
        <span className={`text-[7px] px-1 rounded font-bold uppercase ${
          parsed.type === 'json' ? 'bg-yellow-500/20 text-yellow-400' :
          parsed.type === 'code' ? 'bg-blue-500/20 text-blue-400' : 'bg-slate-700 text-slate-400'
        }`}>{parsed.type}</span>
        <span className="text-[7px] text-slate-500 font-mono flex-1 truncate">{preview}</span>
        <i className={`fas fa-chevron-${collapsed ? 'down' : 'up'} text-[7px] text-slate-600`}></i>
      </button>
      {!collapsed && (
        <pre className="text-[7px] text-emerald-300 font-mono bg-[#060a0e] p-2 overflow-auto max-h-48 leading-relaxed whitespace-pre-wrap">
          {parsed.content.slice(0, 4000)}{parsed.content.length > 4000 && '\n... (truncated)'}
        </pre>
      )}
    </div>
  );
};

const StepRow: React.FC<{
  step: TaskStep; isApprovalPhase: boolean; editingStep: string | null;
  onEdit: (id: string | null) => void; onModelChange: (stepId: string, model: string) => void;
  onRetry: (stepId: string) => void; availableModels: any[];
}> = ({ step, isApprovalPhase, editingStep, onEdit, onModelChange, onRetry, availableModels }) => {
  const [expanded, setExpanded] = useState(false);
  useEffect(() => { if (step.status === 'running') setExpanded(true); }, [step.status]);
  const hasContent = (step.logs?.length > 0) || (step.artifacts?.length > 0);
  const statusIcon: Record<string, string> = {
    finished: '✅', running: '⏳', failed: '❌', approved: '👍', pending: '◻', retry: '🔁', skipped: '⏭'
  };
  return (
    <div className={`bg-[#1a1d2e] border rounded-lg overflow-hidden transition-all ${
      step.status === 'running'  ? 'border-blue-500/50 shadow-[0_0_12px_rgba(59,130,246,0.1)]' :
      step.status === 'finished' ? 'border-green-500/20' :
      step.status === 'failed'   ? 'border-red-500/40' : 'border-[#2a2e39]'
    }`}>
      <div className="flex items-start gap-2 p-2.5 cursor-pointer hover:bg-white/[0.02]"
        onClick={() => hasContent && setExpanded(v => !v)}>
        <span className="text-[9px] font-mono text-slate-600 mt-0.5 shrink-0 w-4">{step.order}.</span>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] text-gray-200 leading-tight">{step.description}</div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            {step.tool_used && <span className="text-[7px] text-slate-500"><i className="fas fa-wrench mr-1 opacity-50"></i>{step.tool_used}</span>}
            {step.phase && <span className="text-[7px] text-slate-600 bg-slate-800 px-1 rounded">{step.phase}</span>}
            {step.artifacts?.length > 0 && <span className="text-[7px] text-emerald-500 bg-emerald-500/10 px-1 rounded border border-emerald-500/20">📎 {step.artifacts.length}</span>}
            {step.logs?.length > 0 && <span className="text-[7px] text-slate-500">📋 {step.logs.length} log{step.logs.length > 1 ? 's' : ''}</span>}
          </div>
          {isApprovalPhase && (
            editingStep === step.step_id ? (
              <div className="mt-1.5 flex gap-1" onClick={e => e.stopPropagation()}>
                <select defaultValue={step.selected_model || 'auto'}
                  onChange={e => onModelChange(step.step_id, e.target.value)}
                  className="flex-1 bg-[#131722] border border-purple-500/50 rounded px-1 py-0.5 text-[8px] text-gray-300 outline-none max-w-[200px]">
                  <option value="auto">🤖 Auto</option>
                  {['fast', 'smart', 'max'].map(tier => {
                    const tm = availableModels.filter(m => m.tier === tier);
                    if (!tm.length) return null;
                    return <optgroup key={tier} label={`Tier ${tier.toUpperCase()}`}>
                      {tm.map(m => <option key={m.id} value={m.id} disabled={m.available === false}>{m.vision ? '👁 ' : ''}{m.model || m.name}</option>)}
                    </optgroup>;
                  })}
                </select>
                <button onClick={e => { e.stopPropagation(); onEdit(null); }} className="text-[8px] text-slate-500 hover:text-slate-300 px-1">✕</button>
              </div>
            ) : (
              <button onClick={e => { e.stopPropagation(); onEdit(step.step_id); }}
                className="mt-0.5 text-[7px] text-slate-600 hover:text-purple-400 transition-colors flex items-center gap-1">
                <i className="fas fa-robot text-[6px]"></i>{step.selected_model || 'auto'}<i className="fas fa-pen text-[6px] ml-0.5"></i>
              </button>
            )
          )}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded border ${STATUS_COLOR[step.status] || STATUS_COLOR.pending}`}>
            {statusIcon[step.status] || '?'} {step.status}
          </span>
          {step.status === 'failed' && (
            <button onClick={e => { e.stopPropagation(); onRetry(step.step_id); }}
              className="text-[7px] bg-orange-500/20 hover:bg-orange-500/40 text-orange-400 px-1.5 py-0.5 rounded border border-orange-500/30 transition-all">
              🔁 Retry
            </button>
          )}
          {hasContent && <i className={`fas fa-chevron-${expanded ? 'up' : 'down'} text-[7px] text-slate-600 mt-0.5`}></i>}
        </div>
      </div>
      {expanded && hasContent && (
        <div className="border-t border-[#2a2e39] bg-[#0d1117] p-2 space-y-2">
          {step.logs?.length > 0 && (
            <div>
              <div className="text-[7px] text-slate-500 uppercase font-bold tracking-widest mb-1">📋 Execution Logs</div>
              <div className="space-y-0.5 max-h-32 overflow-y-auto">
                {step.logs.map((l, i) => (
                  <div key={i} className={`text-[7px] font-mono leading-relaxed px-1 rounded ${
                    l.includes('OK') || l.includes('success') ? 'text-green-400' :
                    l.includes('FAIL') || l.includes('Error') || l.includes('error') ? 'text-red-400' :
                    l.includes('WARNING') || l.includes('LOOP') ? 'text-yellow-400' : 'text-slate-500'
                  }`}>{l}</div>
                ))}
              </div>
            </div>
          )}
          {step.artifacts?.length > 0 && (
            <div>
              <div className="text-[7px] text-slate-500 uppercase font-bold tracking-widest mb-1">📎 Artifacts</div>
              {step.artifacts.map((a, i) => <ArtifactBlock key={i} raw={a} index={i} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AllArtifactsPanel: React.FC<{ plan: TaskStep[] }> = ({ plan }) => {
  const steps = plan.filter(s => s.artifacts?.length > 0);
  if (!steps.length) return (
    <div className="p-3 text-center text-[9px] text-slate-600">
      <i className="fas fa-box-open text-xl mb-2 block opacity-30"></i>Brak artefaktów
    </div>
  );
  return (
    <div className="p-3 space-y-3 overflow-auto">
      {steps.map(step => (
        <div key={step.step_id}>
          <div className="text-[8px] text-slate-400 font-bold mb-1 flex items-center gap-1">
            <span className="text-emerald-400">✅</span> Krok {step.order}: {step.description.slice(0, 60)}{step.description.length > 60 ? '...' : ''}
          </div>
          {step.artifacts.map((a, i) => <ArtifactBlock key={i} raw={a} index={i} />)}
        </div>
      ))}
    </div>
  );
};

// ── SSE event types that trigger fetchTask ──────────────────────────────────
const REFRESH_EVENTS = new Set([
  'task_updated', 'step_finished', 'step_completed', 'step_failed',
  'step_started', 'execution_started', 'task_completed', 'task_created',
]);

interface Props { addLog?: (type: string, msg: string) => void; }

const PlanningOrchestrator: React.FC<Props> = ({ addLog }) => {
  const [phase, setPhase]               = useState<Phase>('idle');
  const [prompt, setPrompt]             = useState('');
  const [profile, setProfile]           = useState('balanced');
  const [plannerModel, setPlannerModel] = useState('auto');
  const [taskId, setTaskId]             = useState<string | null>(null);
  const [task, setTask]                 = useState<Task | null>(null);
  const [logs, setLogs]                 = useState<string[]>([]);
  const [error, setError]               = useState('');
  const [editingStep, setEditingStep]   = useState<string | null>(null);
  const [showThoughts, setShowThoughts] = useState(false);
  const [activeTab, setActiveTab]       = useState<'steps' | 'artifacts' | 'agent'>('steps');
  const [availableModels, setAvailableModels] = useState<any[]>(MODELS_FALLBACK);
  const [agentEvents, setAgentEvents]   = useState<any[]>([]);
  const [sseConnected, setSseConnected] = useState(false);
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const agentPanelRef = React.useRef<HTMLDivElement>(null);
  const taskIdRef     = React.useRef<string | null>(null);
  useEffect(() => { taskIdRef.current = taskId; }, [taskId]);

  const authHeaders = () => ({
    'X-API-Key': localStorage.getItem('omnigate_api_key') || '',
    'Content-Type': 'application/json' as const,
  });

  const addLogLocal = useCallback((msg: string) => {
    setLogs(prev => [...prev.slice(-149), `[${new Date().toLocaleTimeString()}] ${msg}`]);
    addLog?.('INFO', msg);
  }, [addLog]);

  // ── Fetch models ──────────────────────────────────────────────────────────
  useEffect(() => {
    fetch('/v1/omnigate-use/agent/models', { headers: authHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.models) setAvailableModels(d.models);
        else if (d?.tiers) setAvailableModels([...d.tiers.fast, ...d.tiers.smart, ...d.tiers.max]);
      })
      .catch(() => {});
  }, []);

  // ── fetchTask — stable via ref ─────────────────────────────────────────────
  const fetchTask = useCallback(async () => {
    const tid = taskIdRef.current;
    if (!tid) return;
    try {
      const r = await fetch(`/v1/omnigate-use/task/${tid}`, { headers: authHeaders() });
      if (r.ok) { const d = await r.json(); if (d?.task_id) setTask(d); }
    } catch {}
  }, []);

  // ── SSE ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!taskId || phase === 'idle') return;

    const es = new EventSource(`/v1/omnigate-use/task/${taskId}/events`);

    es.onopen = () => setSseConnected(true);

    es.onmessage = (e) => {
      if (!e.data?.trim()) return;
      try {
        const ev = JSON.parse(e.data);
        console.log('[SSE]', ev.type, ev);

        // Agent real-time stream
        if (ev.type === 'agent_thought' || ev.type === 'agent_acting' || ev.type === 'agent_observation') {
          setAgentEvents(prev => {
            const next = [...prev, ev];
            setTimeout(() => {
              if (agentPanelRef.current)
                agentPanelRef.current.scrollTop = agentPanelRef.current.scrollHeight;
            }, 40);
            return next;
          });
          // Switch to agent tab only once (when first event arrives)
          setActiveTab(prev => prev === 'steps' ? 'agent' : prev);
        }

        if (ev.type === 'agent_finished') {
          addLogLocal(`🏁 Agent: ${(ev.result || '').slice(0, 80)}`);
          fetchTask();
        }

        // State refresh triggers
        if (REFRESH_EVENTS.has(ev.type)) fetchTask();

      } catch {}
    };

    es.onerror = () => setSseConnected(false);

    return () => { setSseConnected(false); es.close(); };
  }, [taskId, phase, fetchTask]);

  // ── Fallback poll (5 s) ────────────────────────────────────────────────────
  useEffect(() => {
    if (!taskId || phase === 'idle') return;
    const iv = setInterval(fetchTask, 5000);
    return () => clearInterval(iv);
  }, [taskId, phase, fetchTask]);

  // ── Status transitions ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!task) return;
    const s = task.status;
    if (s === 'awaiting_approval' && phase !== 'awaiting_approval') {
      setPhase('awaiting_approval');
      setShowRejectInput(false); setRejectReason('');
      addLogLocal(`📋 Plan gotowy — ${task.plan.length} kroków`);
    } else if (s === 'executing' && phase !== 'executing') {
      setPhase('executing');
      setActiveTab('steps');
    } else if (s === 'completed' && phase !== 'completed') {
      setPhase('completed');
      addLogLocal('✅ Zadanie zakończone!');
      if (task.plan.some(s => s.artifacts?.length > 0)) setActiveTab('artifacts');
    } else if (s === 'failed') {
      setError(task.planner_thoughts || 'Task failed');
      setPhase('idle');
    }
  }, [task?.status]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!prompt.trim()) return;
    setError(''); setPhase('planning'); setLogs([]); setTask(null); setTaskId(null);
    setAgentEvents([]); setShowRejectInput(false); setRejectReason(''); setActiveTab('steps');
    addLogLocal(`🚀 "${prompt.slice(0, 60)}..." [${profile}]`);
    try {
      const r = await fetch('/v1/omnigate-use/task/create', {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ prompt, profile, model: plannerModel }),
      });
      const d = await r.json();
      if (d?.task_id) { setTaskId(d.task_id); addLogLocal(`📝 Zadanie ${d.task_id.slice(0,8)} — planner...`); }
      else { setError(typeof d === 'string' ? d : JSON.stringify(d)); setPhase('idle'); }
    } catch (e: any) { setError(e.message || 'Network error'); setPhase('idle'); }
  };

  const handleAgentRun = async () => {
    if (!prompt.trim()) return;
    setError(''); setLogs([]); setTask(null); setAgentEvents([]);
    const tid = `agent_${Date.now()}`;
    setTaskId(tid); setPhase('executing'); setActiveTab('agent');
    addLogLocal(`🤖 Agent YOLO: "${prompt.slice(0, 60)}..."`);
    try {
      const r = await fetch('/v1/omnigate-use/agent/run', {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ task: prompt, task_id: tid, max_iters: 25 }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); setError(d.detail || 'Agent run failed'); setPhase('idle'); }
    } catch (e: any) { setError(e.message || 'Network error'); setPhase('idle'); }
  };

  const handleApprove = async () => {
    if (!taskId) return;
    addLogLocal('✅ Zatwierdzam...');
    const r = await fetch(`/v1/omnigate-use/task/${taskId}/approve`, { method: 'POST', headers: authHeaders() });
    if (!r.ok) { const d = await r.json().catch(() => ({})); setError(d.detail || 'Approve failed'); }
  };

  const handleRejectConfirm = async () => {
    if (!taskId) return;
    const reason = rejectReason.trim() || 'User rejected — regenerate with improvements';
    addLogLocal(`🔄 Odrzucam: "${reason.slice(0, 60)}"`);
    setPhase('planning'); setTask(null); setShowRejectInput(false);
    await fetch(`/v1/omnigate-use/task/${taskId}/reject`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify({ reason }),
    });
  };

  const handleCancel = async () => {
    if (!taskId) return;
    await fetch(`/v1/omnigate-use/task/${taskId}/cancel`, { method: 'POST', headers: authHeaders() });
    reset(); addLogLocal('❌ Anulowane');
  };

  const handleRetryStep = async (stepId: string) => {
    if (!taskId) return;
    await fetch(`/v1/omnigate-use/task/${taskId}/step/${stepId}/retry`, { method: 'POST', headers: authHeaders() });
    addLogLocal(`🔁 Retry krok ${stepId.slice(0,6)}`);
  };

  const handleModelChange = async (stepId: string, model: string) => {
    if (!taskId) return;
    await fetch(`/v1/omnigate-use/task/${taskId}/step/${stepId}/model`, {
      method: 'PUT', headers: authHeaders(), body: JSON.stringify({ model }),
    });
    setTask(prev => prev ? {
      ...prev, plan: prev.plan.map(s => s.step_id === stepId ? { ...s, selected_model: model } : s)
    } : prev);
    setEditingStep(null);
  };

  const reset = () => {
    setPhase('idle'); setTaskId(null); setTask(null); setError(''); setAgentEvents([]);
    setEditingStep(null); setShowRejectInput(false); setRejectReason('');
    setActiveTab('steps'); setSseConnected(false);
  };

  const completedSteps = task?.plan.filter(s => ['finished', 'skipped'].includes(s.status)).length ?? 0;
  const totalSteps     = task?.plan.length ?? 0;
  const artifactCount  = task?.plan.reduce((acc, s) => acc + (s.artifacts?.length ?? 0), 0) ?? 0;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="h-full flex flex-col bg-[#0a0c10] text-gray-200 overflow-hidden">

      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#2a2e39] bg-[#131722] shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-base">🧠</span>
          <span className="text-[10px] font-black uppercase tracking-wider text-purple-400">Omnigate Use</span>
          {taskId && <span className="text-[8px] text-slate-500 font-mono bg-slate-900 px-1 rounded">{taskId.slice(0, 8)}</span>}
          {sseConnected && phase !== 'idle' && (
            <span className="flex items-center gap-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
              <span className="text-[7px] text-green-500/70">LIVE</span>
            </span>
          )}
          {phase === 'executing' && totalSteps > 0 &&
            <span className="text-[8px] text-blue-400 font-mono">{completedSteps}/{totalSteps}</span>}
          {artifactCount > 0 &&
            <span className="text-[7px] bg-emerald-500/15 text-emerald-400 px-1.5 py-0.5 rounded border border-emerald-500/25">📎 {artifactCount}</span>}
        </div>
        <div className="flex items-center gap-1.5">
          {task?.planner_thoughts && (
            <button onClick={() => setShowThoughts(v => !v)}
              className="text-[8px] px-1.5 py-0.5 rounded border border-slate-600 bg-slate-800 text-slate-400 hover:text-purple-400 hover:border-purple-500/50 transition-all">
              💭 Thoughts
            </button>
          )}
          {phase !== 'idle' && (
            <button onClick={handleCancel}
              className="text-[9px] bg-red-500/20 hover:bg-red-500/40 text-red-400 px-2 py-1 rounded border border-red-500/30 transition-all">
              ✕ Cancel
            </button>
          )}
        </div>
      </div>

      {showThoughts && task?.planner_thoughts && (
        <div className="shrink-0 bg-[#0d1117] border-b border-purple-500/20 p-2 max-h-28 overflow-y-auto">
          <div className="text-[8px] text-purple-300 font-mono leading-relaxed whitespace-pre-wrap">{task.planner_thoughts}</div>
        </div>
      )}

      {phase === 'executing' && totalSteps > 0 && (
        <div className="h-0.5 bg-slate-800 shrink-0">
          <div className="h-full bg-gradient-to-r from-purple-600 to-blue-600 transition-all duration-1000"
            style={{ width: `${(completedSteps / totalSteps) * 100}%` }} />
        </div>
      )}

      {/* ── IDLE ── */}
      {phase === 'idle' && (
        <div className="flex-1 flex flex-col p-3 gap-3 overflow-auto">
          <textarea value={prompt} onChange={e => setPrompt(e.target.value)}
            placeholder="Opisz zadanie dla agenta AI..."
            className="w-full h-24 bg-[#1a1d2e] border border-[#2a2e39] rounded-lg p-3 text-[11px] text-gray-200 placeholder-gray-600 resize-none focus:border-purple-500 outline-none transition-colors" />
          <div>
            <div className="text-[8px] text-slate-500 uppercase tracking-widest mb-1.5 font-bold">Execution Profile</div>
            <div className="flex gap-1.5 flex-wrap">
              {PROFILES.map(p => (
                <button key={p.id} onClick={() => setProfile(p.id)} title={p.desc}
                  className={`text-[9px] px-2.5 py-1 rounded border transition-all font-bold flex items-center gap-1 ${
                    profile === p.id
                      ? 'bg-purple-500/25 border-purple-500 text-purple-300'
                      : 'bg-[#1a1d2e] border-[#2a2e39] text-gray-500 hover:border-purple-500/30 hover:text-gray-300'
                  }`}><span>{p.emoji}</span><span>{p.name}</span></button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[8px] text-slate-500 uppercase tracking-widest mb-1.5 font-bold">Planner Model</div>
            <select value={plannerModel} onChange={e => setPlannerModel(e.target.value)}
              className="w-full bg-[#1a1d2e] border border-[#2a2e39] rounded px-2 py-1.5 text-[10px] text-gray-300 outline-none focus:border-purple-500 transition-colors">
              <option value="auto">🤖 Auto</option>
              {['fast', 'smart', 'max'].map(tier => {
                const tm = availableModels.filter(m => m.tier === tier);
                if (!tm.length) return null;
                return <optgroup key={tier} label={`Tier ${tier.toUpperCase()}`}>
                  {tm.map(m => <option key={m.id} value={m.id} disabled={m.available === false}>{m.vision ? '👁 ' : ''}{m.model || m.name}</option>)}
                </optgroup>;
              })}
            </select>
          </div>
          <div className="flex gap-2 mt-2">
            <button onClick={handleCreate} disabled={!prompt.trim()}
              className="flex-1 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white font-bold text-[11px] rounded-lg disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-lg">
              🚀 Plan Task
            </button>
            <button onClick={handleAgentRun} disabled={!prompt.trim()}
              className="flex-1 py-2.5 bg-gradient-to-r from-cyan-600 to-emerald-600 hover:from-cyan-500 hover:to-emerald-500 text-white font-bold text-[11px] rounded-lg disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-lg">
              🤖 YOLO Agent
            </button>
          </div>
          {error && <div className="text-[10px] text-red-400 bg-red-500/10 p-2 rounded border border-red-500/20">{error}</div>}
        </div>
      )}

      {/* ── PLANNING ── */}
      {phase === 'planning' && (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 p-6">
          <div className="relative">
            <div className="w-12 h-12 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
            <div className="w-8 h-8 border-2 border-blue-500/50 border-b-transparent rounded-full animate-spin absolute top-2 left-2"
              style={{ animationDirection: 'reverse', animationDuration: '0.8s' }} />
          </div>
          <span className="text-sm text-purple-400 font-bold">Generowanie planu...</span>
          {logs.slice(-3).map((l, i) => <div key={i} className="text-[8px] text-gray-600 font-mono">{l}</div>)}
        </div>
      )}

      {/* ── ACTIVE (approval / executing / completed) ── */}
      {(phase === 'awaiting_approval' || phase === 'executing' || phase === 'completed') && task && (
        <div className="flex-1 flex flex-col overflow-hidden">

          {/* Tabs */}
          <div className="shrink-0 flex border-b border-[#2a2e39] bg-[#0d1117]">
            {(['steps', 'artifacts', 'agent'] as const).map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className={`flex-1 py-1.5 text-[8px] font-bold uppercase tracking-widest transition-colors relative ${
                  activeTab === tab
                    ? tab === 'steps'     ? 'border-b-2 border-purple-500 text-purple-400 bg-purple-500/5'
                    : tab === 'artifacts' ? 'border-b-2 border-emerald-500 text-emerald-400 bg-emerald-500/5'
                    :                       'border-b-2 border-cyan-500 text-cyan-400 bg-cyan-500/5'
                    : 'text-slate-500 hover:text-slate-300'
                }`}>
                {tab === 'steps'     && `⚡ Steps (${totalSteps})`}
                {tab === 'artifacts' && <>📎 Artifacts{artifactCount > 0 && <span className="ml-1 bg-emerald-500/30 text-emerald-300 text-[6px] px-1 rounded-full">{artifactCount}</span>}</>}
                {tab === 'agent'     && <>🤖 Agent Real-time{agentEvents.length > 0 && <span className="ml-1 animate-pulse bg-cyan-500/40 text-cyan-200 text-[6px] px-1 rounded-full">LIVE</span>}</>}
              </button>
            ))}
          </div>

          {/* Status banners */}
          {phase === 'awaiting_approval' && (
            <div className="shrink-0 mx-3 mt-2 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded text-[9px] text-yellow-300">
              ⚠️ Plan gotowy — przejrzyj {task.plan.length} kroków i zatwierdź lub odrzuć
            </div>
          )}
          {phase === 'completed' && (
            <div className="shrink-0 mx-3 mt-2 p-2 bg-green-500/10 border border-green-500/30 rounded text-[9px] text-green-300">
              ✅ Zadanie zakończone — {completedSteps}/{totalSteps} kroków · {artifactCount} artefaktów
              {artifactCount > 0 && <button onClick={() => setActiveTab('artifacts')} className="ml-2 underline text-emerald-400">Pokaż →</button>}
            </div>
          )}

          {/* Tab content */}
          <div className="flex-1 overflow-auto">
            {activeTab === 'steps' && (
              <div className="p-3 space-y-1.5">
                {task.plan.map(step => (
                  <StepRow key={step.step_id} step={step} isApprovalPhase={phase === 'awaiting_approval'}
                    editingStep={editingStep} onEdit={setEditingStep}
                    onModelChange={handleModelChange} onRetry={handleRetryStep}
                    availableModels={availableModels} />
                ))}
              </div>
            )}
            {activeTab === 'artifacts' && <AllArtifactsPanel plan={task.plan} />}
            {activeTab === 'agent' && (
              <div ref={agentPanelRef} className="p-3 font-mono text-[8px] space-y-1.5 bg-black/40 h-full overflow-auto">
                {agentEvents.length === 0 ? (
                  <div className="text-slate-600 text-center py-10">
                    <span className="block text-2xl mb-2 opacity-30">🤖</span>
                    <span className="italic">Oczekiwanie na eventy agenta...</span>
                    <span className={`block text-[7px] mt-1 ${sseConnected ? 'text-green-600' : 'text-slate-700'}`}>
                      {sseConnected ? '● SSE połączony' : '○ Łączenie...'}
                    </span>
                    {phase === 'awaiting_approval' && (
                      <span className="block text-[7px] text-yellow-700 mt-2">Kliknij "Approve &amp; Execute" aby uruchomić agenta</span>
                    )}
                  </div>
                ) : (
                  agentEvents.map((ev, i) => (
                    <div key={i} className={`border-l-2 pl-2 pb-1.5 ${
                      ev.type === 'agent_thought'      ? 'border-purple-800' :
                      ev.type === 'agent_acting'       ? 'border-yellow-800' :
                      ev.type === 'agent_observation'  ? 'border-emerald-900' :
                                                         'border-slate-800'
                    }`}>
                      <div className="flex items-center gap-1 mb-0.5">
                        <span className={`text-[6px] px-1 rounded font-bold uppercase ${
                          ev.type === 'agent_thought'     ? 'text-purple-400 bg-purple-950' :
                          ev.type === 'agent_acting'      ? 'text-yellow-400 bg-yellow-950' :
                          ev.type === 'agent_observation' ? 'text-emerald-400 bg-emerald-950' :
                                                            'text-slate-400 bg-slate-900'
                        }`}>{ev.type.replace('agent_', '')}</span>
                        {ev.step_id && <span className="text-[6px] text-slate-700">#{ev.step_id?.slice(0,6)}</span>}
                        {ev.model && <span className="text-[6px] text-slate-700">{ev.model}</span>}
                      </div>
                      {ev.thought && <div className="text-slate-300 italic leading-relaxed">{ev.thought}</div>}
                      {ev.action && (
                        <div className="text-yellow-400 mt-0.5">
                          <span className="text-yellow-700">→</span> {ev.action}
                          {ev.action_input && Object.keys(ev.action_input).length > 0 &&
                            <span className="text-slate-600"> ({JSON.stringify(ev.action_input).slice(0, 80)})</span>}
                        </div>
                      )}
                      {ev.observation && (
                        <div className="text-emerald-400 mt-0.5 truncate">
                          <span className="text-emerald-800">●</span> {ev.observation}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Bottom actions */}
          {phase === 'awaiting_approval' && (
            <div className="shrink-0 border-t border-[#2a2e39]">
              {showRejectInput ? (
                <div className="p-3 bg-[#0d1117] space-y-2">
                  <div className="text-[9px] text-orange-400 font-bold">💬 Feedback dla planera:</div>
                  <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                    placeholder="np. Zamiast browser_use użyj code-executor. Uprość do 3 kroków..."
                    autoFocus
                    className="w-full h-16 bg-[#1a1d2e] border border-orange-500/40 rounded p-2 text-[10px] text-gray-200 placeholder-gray-600 resize-none focus:border-orange-500 outline-none transition-colors" />
                  <div className="flex gap-2">
                    <button onClick={handleRejectConfirm}
                      className="flex-1 py-2 bg-orange-600/30 hover:bg-orange-600/50 text-orange-300 font-bold text-[10px] rounded-lg border border-orange-500/40 transition-all">
                      🔄 Odrzuć i generuj nowy plan
                    </button>
                    <button onClick={() => { setShowRejectInput(false); setRejectReason(''); }}
                      className="py-2 px-3 bg-slate-700/50 hover:bg-slate-700 text-slate-400 font-bold text-[10px] rounded-lg border border-slate-600 transition-all">✕</button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2 p-3">
                  <button onClick={handleApprove}
                    className="flex-1 py-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-bold text-[10px] rounded-lg transition-all shadow-[0_0_12px_rgba(34,197,94,0.2)]">
                    ✅ Approve &amp; Execute
                  </button>
                  <button onClick={() => setShowRejectInput(true)}
                    className="py-2 px-3 bg-orange-600/20 hover:bg-orange-600/40 text-orange-300 font-bold text-[10px] rounded-lg border border-orange-500/30 transition-all">
                    🔄 Reject
                  </button>
                </div>
              )}
            </div>
          )}

          {phase === 'completed' && (
            <div className="shrink-0 p-3 border-t border-[#2a2e39]">
              <button onClick={reset}
                className="w-full py-2 bg-purple-600/20 hover:bg-purple-600/40 text-purple-300 font-bold text-[10px] rounded-lg border border-purple-500/30 transition-all">
                🔄 New Task
              </button>
            </div>
          )}
        </div>
      )}

      {/* Activity log */}
      <div className="h-14 border-t border-[#2a2e39] px-2 py-1 overflow-y-auto bg-[#07090d] shrink-0">
        <div className="text-[6px] text-slate-600 uppercase font-bold mb-0.5 tracking-widest">Activity Log</div>
        {logs.slice(-8).map((l, i) => (
          <div key={i} className="text-[7px] text-gray-600 font-mono leading-relaxed">{l}</div>
        ))}
      </div>
    </div>
  );
};

export default PlanningOrchestrator;
